import { Badge } from '@artosera/ui';
import { Check } from 'lucide-react';
import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import type { DemandeSiteVue } from '../../api/demandeSite.js';
import styles from './DemandeSitePage.module.css';

/** La date d'une demande, dans la langue de la régie. */
export function useDateDemande(): (iso: string) => string {
  const { i18n } = useTranslation();
  return (iso) =>
    new Intl.DateTimeFormat(i18n.language, { dateStyle: 'long' }).format(new Date(iso));
}

/**
 * La demande ouverte : où elle en est, puis ce qui a été envoyé. Elle remplace
 * le formulaire tant que l'équipe Artosera ne l'a pas traitée.
 */
export function DemandeSiteEnCours({ demande }: { demande: DemandeSiteVue }): JSX.Element {
  const { t } = useTranslation('regie');
  const date = useDateDemande();
  const priseEnCharge = demande.etat === 'en_cours';

  const etapes = [
    { cle: 'recue', etat: priseEnCharge ? 'fait' : 'courant' },
    { cle: 'priseEnCharge', etat: priseEnCharge ? 'courant' : 'avenir' },
  ] as const;

  const lignes: readonly (readonly [string, string | readonly string[] | null])[] = [
    [
      t('demandeSite.recap.siteActuel'),
      demande.siteExistant ? demande.urlActuelle : t('demandeSite.recap.aucunSite'),
    ],
    [
      t('demandeSite.recap.langues'),
      demande.langues.map((langue) => t(`demandeSite.formulaire.langues.${langue}`)).join(', '),
    ],
    [
      t('demandeSite.recap.visite'),
      t(demande.visiteVirtuelle ? 'demandeSite.recap.oui' : 'demandeSite.recap.non'),
    ],
    [t('demandeSite.recap.delai'), t(`demandeSite.formulaire.delai.${demande.delai}`)],
    [t('demandeSite.recap.budget'), demande.budget],
    [
      t('demandeSite.recap.contact'),
      [demande.contact.nom, demande.contact.email, demande.contact.telephone].filter(
        (ligne): ligne is string => Boolean(ligne),
      ),
    ],
    [
      t('demandeSite.recap.moyen'),
      t(`demandeSite.formulaire.contact.moyen.${demande.contact.moyen}`),
    ],
    [t('demandeSite.recap.objectifs'), demande.objectifs],
    [t('demandeSite.recap.message'), demande.message === '' ? null : demande.message],
  ];

  return (
    <section className={styles.enCours} aria-labelledby="demande-site-en-cours">
      <header className={styles.enCoursEntete}>
        <h2 id="demande-site-en-cours" className={styles.titrePanneau}>
          {t('demandeSite.enCours.titre', { date: date(demande.envoyeeLe) })}
        </h2>
        <Badge tone={priseEnCharge ? 'accent' : 'reserved'}>
          {t(`demandeSite.enCours.etat.${demande.etat as 'envoyee' | 'en_cours'}`)}
        </Badge>
      </header>

      <ol className={styles.etapes} aria-label={t('demandeSite.enCours.etapes.aria')}>
        {etapes.map(({ cle, etat }) => (
          <li
            key={cle}
            className={styles.etape}
            data-etat={etat}
            aria-current={etat === 'courant' ? 'step' : undefined}
          >
            <span className={styles.etapeMarque} aria-hidden="true">
              {etat === 'fait' ? <Check size={14} strokeWidth={2} /> : null}
            </span>
            {t(`demandeSite.enCours.etapes.${cle}`)}
          </li>
        ))}
      </ol>

      <h3 className={styles.sousTitre}>{t('demandeSite.recap.titre')}</h3>
      <dl className={styles.recap}>
        {lignes
          .filter(([, valeur]) => valeur !== null && valeur !== '')
          .map(([libelle, valeur]) => (
            <div key={libelle} className={styles.recapLigne}>
              <dt>{libelle}</dt>
              <dd>
                {typeof valeur === 'string' || valeur === null
                  ? valeur
                  : valeur.map((ligne) => (
                      <span key={ligne} className={styles.recapBloc}>
                        {ligne}
                      </span>
                    ))}
              </dd>
            </div>
          ))}
      </dl>
      <p className={styles.aide}>{t('demandeSite.enCours.aide')}</p>
    </section>
  );
}
